import { useState, useEffect, useRef, Fragment } from "react";
import Select from "react-select";
import { updateUserGuestQuotation } from "../../../../../actions/admin/quotation";
import { getLocationByAddress } from "../../../../../actions/googleAPI";
import AdminModal from "../../../components/AdminModal";
import AdminInput from "../../../components/AdminInput";
import CountryFlag from "../../../../common/CountryFlag";

const PRONOUNCE = [
    { label: "-", value: "-" },
    { label: "Mr.", value: "Mr." },
    { label: "Ms.", value: "Ms." },
    { label: "Mrs.", value: "Mrs." },
    { label: "Mr./Ms.", value: "Mr./Ms." },
];

const customStylesSelect = {
    control: (provided) => ({
        ...provided,
        minHeight: "48px",
        height: "38px",
        marginTop: "10px",
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: "38px",
        padding: "0 8px",
    }),
    indicatorsContainer: (provided) => ({
        ...provided,
        height: "38px",
    }),
};

const stripParentheses = (value) =>
    value ? value.replace(/[()]/g, "").trim() : "";

const getInitialForm = (client) => {
    if (!client) return {
        honorific: "-",
        name: "",
        email: "",
        prefixNumber: "+81",
        phoneNumber: "",
        country: "Japan",
        countryCode: "JP",
        postalCode: "",
        billingAddress: "",
        city: "",
    };

    const detail =
        client.role === "individual"
            ? client.individualDetail
            : client.businessDetail;
    const billing = detail?.billingInformation || {};

    return {
        honorific: billing.pronounced || "-",
        name: billing.name || client.name || "",
        email: client.email || "",
        prefixNumber: stripParentheses(billing.prefix_number) || "+81",
        phoneNumber: billing.phone || "",
        country: billing.country || "Japan",
        countryCode: billing.country_code || "JP",
        postalCode: billing.postal_code === "-" ? "" : (billing.postal_code || ""),
        billingAddress: billing.address || "",
        city: billing.city || "",
    };
};

const EditClientModal = ({ show, client, onClose, onSuccessEditClient, onFailedEditClient }) => {
    const [searchPhoneCode, setSearchPhoneCode] = useState(null);
    const [form, setForm] = useState(getInitialForm(client));
    const [countriesData, setCountriesData] = useState([]);
    const [formValidation, setFormValidation] = useState({ name: true, billingAddress: true });
    const isCountryChangeRef = useRef(false);

    useEffect(() => {
        fetch("/json/countries.json")
            .then((r) => r.json())
            .then(setCountriesData)
            .catch(console.error);
    }, []);

    // Re-initialise form whenever the modal opens with a (potentially different) client
    useEffect(() => {
        if (show) {
            isCountryChangeRef.current = false;
            setForm(getInitialForm(client));
            setFormValidation({ name: true, billingAddress: true });
            setSearchPhoneCode(null);
        }
    }, [show, client]);

    const countryWithoutPostCode = process.env.REACT_APP_COUNTRY_WITHOUT_POSTCODE || "";
    const isPostalCodeDisabled = countryWithoutPostCode.split(",").includes(form.countryCode);
    const isCityDisabled = !isPostalCodeDisabled;

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "name" || name === "billingAddress") {
            const hasInvalidChars = value
                .split("")
                .some((char) => !/[\w\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(char));

            setFormValidation((prev) => ({
                ...prev,
                [name]: value === "" || !hasInvalidChars,
            }));
        }

        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleCountryCodeChange = (dialCode, countryCode, name) => {
        isCountryChangeRef.current = true;
        setForm((prev) => ({
            ...prev,
            country: name,
            countryCode,
            prefixNumber: dialCode,
            postalCode: countryWithoutPostCode.split(",").includes(countryCode) ? "-" : "",
            city: "",
        }));
    };

    const handlePostalCodeBlur = async () => {
        if (!form.postalCode || form.postalCode === "-") return;
        try {
            const result = await getLocationByAddress(form.postalCode, form.countryCode);
            const cityComponent = result.address_components.find(
                (c) => c.types.includes("administrative_area_level_2") || c.types.includes("locality")
            );
            const city = cityComponent
                ? cityComponent.long_name
                : result.address_components[result.address_components.length - 2]?.long_name || "";
            setForm((prev) => ({ ...prev, city }));
        } catch (e) {
            onFailedEditClient(e);
        }
    };

    const handleSubmit = async () => {
        if (!form.email) {
            return;
        }
        const allValid = Object.values(formValidation).every(Boolean);
        if (!allValid) return;

        try {
            const response = await updateUserGuestQuotation(form);
            onSuccessEditClient(response);
        } catch (e) {
            onFailedEditClient(e);
        }
    };

    const filteredCountries = searchPhoneCode
        ? countriesData.filter(
            (c) =>
                c.name.toLowerCase().includes(searchPhoneCode.toLowerCase()) ||
                c.dial_code.includes(searchPhoneCode)
        )
        : countriesData;

    return (
        <AdminModal
            show={show}
            title="Edit Client"
            size="modal-lg"
            onClose={onClose}
            description="Kindly enter the client's name and address using the Roman alphabet only (e.g., Tanaka Taro)"
        >
            <div className="row px-2">
                <div className="col-3 m-0 p-0 pe-1">
                    <p className="mb-2">Honorifics</p>
                    <Select
                        options={PRONOUNCE}
                        value={PRONOUNCE.find((p) => p.value === form.honorific) || null}
                        onChange={(e) =>
                            handleChange({ target: { name: "honorific", value: e.value } })
                        }
                        styles={customStylesSelect}
                        name="honorific"
                    />
                </div>
                <div className="col m-0 p-0 ps-1">
                    <label className="mb-2 fs-14">Name</label>
                    <AdminInput
                        placeholder="Name"
                        value={form.name}
                        onChange={handleChange}
                        name="name"
                        isInvalid={!formValidation.name}
                        invalidMessage="Kindly enter the client's name and address using the Roman alphabet only (e.g., Tanaka Taro)"
                    />
                </div>
            </div>

            <div className="row px-2">
                <div className="col-6 m-0 p-0 pe-1">
                    <label className="mb-2 fs-14">Email</label>
                    <AdminInput
                        placeholder="Email"
                        value={form.email}
                        onChange={() => { }}
                        name="email"
                        disable={true}
                    />
                </div>
                <div className="col-6 m-0 p-0 ps-1">
                    <label className="mb-2 fs-14">Phone No.</label>
                    <div className="d-flex">
                        <button
                            className="btn bg-gold-light-100 fw-100 rounded noto dropdown-toggle"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            style={{ width: "130px" }}
                        >
                            <CountryFlag
                                code={form.countryCode}
                                emoji={countriesData?.find((c) => c.code === form.countryCode)?.flag}
                                alt=""
                            />{" "}
                            ({form.prefixNumber})
                        </button>
                        <ul className="dropdown-menu">
                            <div className="px-1 align-items-center mb-3 dropdown-search">
                                <input
                                    placeholder="Please type in your country name"
                                    type="text"
                                    className="form-control p-3 mb-2"
                                    onChange={(e) => setSearchPhoneCode(e.target.value)}
                                />
                            </div>
                            {filteredCountries.map((country) => (
                                <Fragment key={country.code}>
                                    <li
                                        className="dropdown-item m-0"
                                        onClick={() =>
                                            handleCountryCodeChange(
                                                country.dial_code,
                                                country.code,
                                                country.name
                                            )
                                        }
                                    >
                                        <div className="row px-1 align-items-center">
                                            <div className="col-auto">
                                                <div className="bg-secondary bg-opacity-25 px-1">
                                                    <CountryFlag
                                                        code={country.code}
                                                        emoji={country.flag}
                                                        alt={country.name}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col">
                                                ({country.dial_code}) {country.name}
                                            </div>
                                        </div>
                                    </li>
                                    <hr className="my-1" />
                                </Fragment>
                            ))}
                        </ul>
                        <AdminInput
                            placeholder="Phone No."
                            value={form.phoneNumber}
                            onChange={handleChange}
                            name="phoneNumber"
                            mb="mb-0 w-100"
                        />
                    </div>
                </div>
            </div>

            <div className="row px-2">
                <div className="col-4 m-0 p-0 pe-1">
                    <label className="mb-2 fs-14">Country</label>
                    <AdminInput
                        placeholder="Country"
                        value={form.country}
                        onChange={() => { }}
                        name="country"
                        disable={true}
                    />
                </div>
                <div className="col-4 m-0 p-0 px-1">
                    <label className="mb-2 fs-14">Postal Code</label>
                    <AdminInput
                        placeholder="Postal Code"
                        value={form.postalCode}
                        onChange={handleChange}
                        name="postalCode"
                        disable={isPostalCodeDisabled}
                        onBlur={handlePostalCodeBlur}
                    />
                </div>
                <div className="col-4 m-0 p-0">
                    <label className="mb-2 fs-14">City</label>
                    <AdminInput
                        placeholder="City"
                        value={form.city}
                        onChange={handleChange}
                        name="city"
                        disable={isCityDisabled}
                    />
                </div>
            </div>
            <div className="row px-2">
                <div className="col-12 m-0 p-0">
                    <label className="mb-2 fs-14">Billing Address</label>
                    <AdminInput
                        placeholder="Billing Address"
                        value={form.billingAddress}
                        onChange={handleChange}
                        name="billingAddress"
                        isInvalid={!formValidation.billingAddress}
                        invalidMessage="Kindly enter the client's name and address using the Roman alphabet only (e.g., Tanaka Taro)"
                    />
                </div>
            </div>

            <div className="d-grid mt-3">
                <button className="btn rounded bg-gold py-2" onClick={handleSubmit}>
                    <span className="text-white">Save</span>
                </button>
            </div>
        </AdminModal>
    );
};

export default EditClientModal;
